import React from "react";
import { useAuth } from "@/context/AuthContext";
import { LANDING } from "@/constants/testIds";
import { UnoCardView } from "@/components/UnoCard";
import { motion } from "framer-motion";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Landing() {
  const { user } = useAuth();

  const handleSignIn = () => {
    const redirectUrl = window.location.origin + "/lobby";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  React.useEffect(() => {
    if (user) window.location.replace("/lobby");
  }, [user]);

  return (
    <div className="relative min-h-screen w-full overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-24 -left-20 w-72 h-72 rounded-full blur-3xl opacity-30" style={{background:"var(--uno-blue)"}}/>
        <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full blur-3xl opacity-20" style={{background:"var(--uno-red)"}}/>
      </div>

      <header className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6">
        <div className="flex items-center gap-2 font-display text-xl tracking-tighter">
          <span className="inline-block w-8 h-8 rounded-md" style={{background: "var(--uno-red)"}}/>
          <span>D<span style={{color:"var(--uno-yellow)"}}>O</span>S</span>
        </div>
        <div className="hidden sm:flex gap-6 text-xs uppercase tracking-[0.2em] text-white/60">
          <span>Real-time</span>
          <span>Bots</span>
          <span>Leaderboards</span>
        </div>
      </header>

      <main className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center px-6 sm:px-10 pt-4 pb-16 max-w-7xl mx-auto">
        <div className="lg:col-span-7">
          <div className="text-xs uppercase tracking-[0.3em] text-white/50 mb-6">Play with friends · Ranked · Free</div>
          <h1 data-testid={LANDING.heroTitle} className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter">
            One better<br/>
            than <span style={{color:"var(--uno-yellow)"}}>Uno.</span><br/>
            It's <span style={{color:"var(--uno-red)"}}>Dos.</span>
          </h1>
          <p className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed">
            Uno-style card battles and full Skip-Bo — reborn for the internet age.
            Private rooms, custom rules, live chat, AI bots when your friends bail,
            and a leaderboard that never forgets who lost.
          </p>

          <div className="mt-10 flex flex-wrap gap-4 items-center">
            <button
              data-testid={LANDING.googleSignInBtn}
              onClick={handleSignIn}
              className="group relative inline-flex items-center gap-3 px-7 py-4 rounded-full bg-white text-black font-semibold hover:bg-white/90 transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden>
                <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.9 32.7 29.4 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
                <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.2 19 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.4 4 9.8 8.3 6.3 14.7z"/>
                <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.8-3.3-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"/>
                <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.3 4.1-4.1 5.5l6.2 5.2C41.5 35.8 44 30.4 44 24c0-1.3-.1-2.4-.4-3.5z"/>
              </svg>
              <span>Continue with Google</span>
              <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
            </button>
            <div className="text-white/50 text-sm">7-day sessions. No spam. No cards up our sleeve.</div>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
            {[
              ["2-4", "Players"],
              ["108", "Cards"],
              ["∞", "Rematches"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="font-display text-4xl" style={{color:"var(--uno-red)"}}>{n}</div>
                <div className="text-xs uppercase tracking-widest text-white/50 mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative min-h-[420px]">
          <motion.div initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6 }}
            className="absolute right-4 top-4 rotate-[-8deg]">
            <UnoCardView card={{ color: "red", value: "7" }} />
          </motion.div>
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.1 }}
            className="absolute right-24 top-24 rotate-[6deg]">
            <UnoCardView card={{ color: "yellow", value: "skip" }} />
          </motion.div>
          <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
            className="absolute right-52 top-10 rotate-[-14deg]">
            <UnoCardView card={{ color: "wild", value: "wild4" }} />
          </motion.div>
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.3 }}
            className="absolute right-8 top-56 rotate-[12deg]">
            <UnoCardView card={{ color: "green", value: "draw2" }} />
          </motion.div>
          <motion.div initial={{ y: 120, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.7, delay: 0.4 }}
            className="absolute right-40 top-64 rotate-[-4deg]">
            <UnoCardView card={{ color: "blue", value: "4" }} />
          </motion.div>
        </div>
      </main>

      <footer className="relative z-10 px-6 sm:px-10 py-6 text-xs text-white/40 flex justify-between max-w-7xl mx-auto">
        <span>© 2026 DOS</span>
        <span>Made with parallel tool calls & petty vengeance</span>
      </footer>
    </div>
  );
}
